import { waitForNarrationBoundary } from "@/desktop/narration-boundary";
import { track } from "@/desktop/observability";
import { useTTS } from "@/desktop/use-tts";
import { abortableDelay } from "@/lib/abortable-delay";
import { recordSessionLatency } from "@/lib/session-latency-trace";
import type {
  AgentPresentation,
  AgentPresentationStep,
} from "@/lib/agent-presentation-types";
import { useTtsSettingsStore } from "@/stores/tts-settings-store";

import { preparePresentationSpeech } from "@/desktop/tts-presentation-preparation";
import { reportStepProgress } from "./presentation-step-progress";
import { PRESENTATION_FINISHED } from "./presentation-finished";

function speechSource(
  presentation: AgentPresentation,
  step: AgentPresentationStep,
): string {
  return `presentation:${encodeURIComponent(presentation.executionId ?? "")}:${encodeURIComponent(presentation.presentationId)}:${encodeURIComponent(step.step_id)}:${presentation.playbackAttempt ?? 1}:${presentation.speechAttempt ?? 0}${"speech_role" in step && step.speech_role === "progress" ? ":progress" : ""}`;
}
export function preparePresentationNarration(
  presentation: AgentPresentation,
  step: AgentPresentationStep,
  signal: AbortSignal,
): void {
  const text = narrationText(step);
  if (text)
    preparePresentationSpeech(speechSource(presentation, step), text, signal);
}

function narrationText(step: AgentPresentationStep): string | null {
  if (step.kind === "pause" || step.kind === "clear") return null;
  return (
    step.narration?.trim() ||
    ((step.kind === "draw" || step.kind === "highlight") &&
      step.callout?.trim()) ||
    step.bubble?.trim() ||
    null
  );
}

export function narrationFallbackMs(
  text: string,
  pace: "quick" | "normal" | "slow" = "normal",
): number {
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  const paceFactor = pace === "quick" ? 0.78 : pace === "slow" ? 1.3 : 1;
  return Math.round(Math.max(1_200, words * 330 * paceFactor));
}

type PreviousStep = {
  presentationId: string;
  stepId: string;
  kind: AgentPresentationStep["kind"];
};
const previousSteps = new Map<string, PreviousStep>();
const PREVIOUS_STEPS_KEPT = 16;

/**
 * The step this execution ran before `step`, then remembers `step` in its
 * place. A replay of the same step finds itself, which is not "previous", so a
 * replay is never silenced by its own first attempt. Each `then` hop is a
 * one-step presentation, so this spans hops within the window that narrates.
 */
function takePreviousStep(
  presentation: AgentPresentation,
  step: AgentPresentationStep,
): PreviousStep | undefined {
  const executionId = presentation.executionId ?? "";
  const stored = previousSteps.get(executionId);
  if (
    stored?.presentationId === presentation.presentationId &&
    stored.stepId === step.step_id
  )
    return undefined;
  previousSteps.delete(executionId);
  previousSteps.set(executionId, {
    presentationId: presentation.presentationId,
    stepId: step.step_id,
    kind: step.kind,
  });
  if (previousSteps.size > PREVIOUS_STEPS_KEPT)
    previousSteps.delete(previousSteps.keys().next().value!);
  return stored;
}

/**
 * OPERATE mode: the action never waits to be announced. Measured: sentences
 * held fast steps for as long as they took to say, and piled up behind them.
 * The words are offered beside the action; the owner window drops them if a
 * sentence of this execution is still playing or queued, so nothing is cut
 * off and nothing lags behind. The bubble still shows either way.
 */
function narrateAlongsideAction(
  presentation: AgentPresentation,
  step: AgentPresentationStep,
  signal: AbortSignal,
  text: string,
  previous: PreviousStep | undefined,
  context: { used_bubble_fallback: boolean; pace: string },
): Promise<boolean> {
  const source = speechSource(presentation, step);
  // A second scroll or key straight after the first says nothing new. Pastes
  // and typing carry what is being entered, so they always speak.
  if (
    (step.kind === "scroll" || step.kind === "key") &&
    previous?.kind === step.kind
  ) {
    useTTS.getState().cancelPresentationSpeech(source);
    return reportStepProgress(
      presentation,
      step,
      "speech",
      "skipped",
      "repeated_step",
    );
  }
  const settings = useTtsSettingsStore.getState();
  if (
    !settings.enabled ||
    !settings.premiumAvailable ||
    !settings.denkerVoiceId
  )
    return reportStepProgress(
      presentation,
      step,
      "speech",
      "skipped",
      "voice_unavailable",
    );
  // Silenced by Stop, a failure or a cancelled run - not by the run simply
  // finishing, which would cut every sentence off as the action landed.
  signal.addEventListener(
    "abort",
    () => {
      if (signal.reason !== PRESENTATION_FINISHED)
        useTTS.getState().cancelPresentationSpeech(source);
    },
    { once: true },
  );
  track("desktop_presentation_narration_requested", {
    presentation_id: presentation.presentationId,
    step_id: step.step_id,
    ...context,
    alongside_action: true,
  });
  useTTS.getState().speak(text, null, {
    executionId: presentation.executionId,
    ttsSource: source,
    dropIfBusy: true,
  });
  return reportStepProgress(
    presentation,
    step,
    "speech",
    "skipped",
    "spoken_alongside_action",
  );
}

export async function narratePresentationStep(
  presentation: AgentPresentation,
  step: AgentPresentationStep,
  signal: AbortSignal,
): Promise<boolean> {
  if (signal.aborted) return false;
  // Every step counts as "previous", spoken or not: a click with nothing to
  // say still separates two scrolls.
  const previous =
    presentation.mode === "operate"
      ? takePreviousStep(presentation, step)
      : undefined;
  const text = narrationText(step);
  if (!text)
    return reportStepProgress(
      presentation,
      step,
      "speech",
      "skipped",
      "no_narration",
    );
  const pace = "pace" in step ? step.pace : undefined;
  const usedBubbleFallback =
    !("narration" in step && step.narration?.trim()) &&
    "bubble" in step &&
    Boolean(step.bubble?.trim()) &&
    !(
      (step.kind === "draw" || step.kind === "highlight") &&
      step.callout?.trim()
    );
  if (presentation.mode === "operate")
    return narrateAlongsideAction(presentation, step, signal, text, previous, {
      used_bubble_fallback: usedBubbleFallback,
      pace: pace ?? "normal",
    });
  const settings = useTtsSettingsStore.getState();
  const fallbackMs = narrationFallbackMs(text, pace);
  if (
    !settings.enabled ||
    !settings.premiumAvailable ||
    !settings.denkerVoiceId
  ) {
    track("desktop_presentation_narration_unavailable", {
      presentation_id: presentation.presentationId,
      step_id: step.step_id,
      used_bubble_fallback: usedBubbleFallback,
      tts_enabled: settings.enabled,
      premium_available: settings.premiumAvailable,
      voice_configured: Boolean(settings.denkerVoiceId),
    });
    await abortableDelay(fallbackMs, signal);
    return (
      !signal.aborted &&
      (await reportStepProgress(
        presentation,
        step,
        "speech",
        "skipped",
        "voice_unavailable",
      ))
    );
  }

  if (!(await reportStepProgress(presentation, step, "speech", "started")))
    return false;
  const source = speechSource(presentation, step);
  const startedAt = Date.now();
  // Keep the watchdog beyond a full reading, including long callouts.
  const words = text.split(/\s+/).length;
  const maxMs = Math.max(25_000, words * 600 + 12_000);
  track("desktop_presentation_narration_requested", {
    presentation_id: presentation.presentationId,
    step_id: step.step_id,
    used_bubble_fallback: usedBubbleFallback,
    pace: pace ?? "normal",
  });
  const trace = (phase: string, outcome?: string) =>
    recordSessionLatency("presentation.narration", {
      presentationId: presentation.presentationId,
      executionId: presentation.executionId,
      stepId: step.step_id,
      phase,
      outcome,
      durationMs: Date.now() - startedAt,
    });
  const stopSpeech = () => {
    trace("cancel_requested", "presentation_aborted");
    useTTS.getState().cancelPresentationSpeech(source);
  };
  let speechRequested = false;
  const outcome = await waitForNarrationBoundary({
    text,
    signal,
    source,
    startedAt,
    minVisibleMs: 0,
    // Native generation and an existing utterance can delay playback. Keep
    // the mark in place until the matching started/terminal events arrive.
    startTimeoutMs: 25_000,
    maxMs,
    onReady: () => {
      if (signal.aborted) return;
      speechRequested = true;
      trace("requested");
      signal.addEventListener("abort", stopSpeech, { once: true });
      useTTS.getState().speak(text, null, {
        executionId: presentation.executionId,
        ttsSource: source,
      });
    },
  });
  if (speechRequested) signal.removeEventListener("abort", stopSpeech);
  if (["fallback", "timeout", "error"].includes(outcome)) {
    trace("cancel_requested", outcome);
    useTTS.getState().cancelPresentationSpeech(source);
  }
  trace("boundary_settled", outcome);
  track("desktop_presentation_narration_finished", {
    presentation_id: presentation.presentationId,
    step_id: step.step_id,
    outcome,
    elapsed_ms: Date.now() - startedAt,
  });
  if (signal.aborted || outcome === "aborted") return false;
  if (outcome === "interrupted")
    return reportStepProgress(
      presentation,
      step,
      "speech",
      "skipped",
      "user_speech",
    );
  if (outcome !== "finished") {
    await reportStepProgress(
      presentation,
      step,
      "speech",
      outcome === "cancelled" ? "cancelled" : "failed",
      outcome,
    );
    return false;
  }
  return reportStepProgress(presentation, step, "speech", "finished");
}
